import React, { useState, useRef, useEffect, useCallback } from "react";
import { toast } from "sonner";
import {
  Plus,
  Image as ImageIcon,
  Upload,
  X,
  Search,
  Loader2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { v4 as uuidv4 } from 'uuid';
import api from '../../lib/axios';

const API = process.env.REACT_APP_BACKEND_URL;

const AddItemModal = ({ isOpen, onClose, onAdd }) => {
  const fileInputRef = useRef(null);
  const descriptionInputRef = useRef(null);
  const popupRef = useRef(null);
  
  const [imagePreview, setImagePreview] = useState(null);
  const [formData, setFormData] = useState({
    item_code: "",
    description: "",
    series: "",
    material: "uPVC",
    color: "",
    width_m: "",
    height_m: "",
    quantity: 1,
    cost_per_m2: "",
    observations: "",
    image_data: null,
    matched_type_id: null
  });

  // Autocomplete states
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [popupSize, setPopupSize] = useState({ width: 450, height: 300 });
  const [isResizing, setIsResizing] = useState(false);

  // Debounced search for descriptions
  const searchDescriptions = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setSuggestions([]);
      return;
    }

    setIsSearching(true);
    try {
      const response = await api.get(`${API}/api/quote-types`, {
        params: { skip: 0, limit: 100 }
      });
      
      const allTypes = response.data || [];
      const filtered = allTypes.filter(type => {
        const name = (type.name || '').toLowerCase();
        const description = (type.description || '').toLowerCase();
        const searchTerm = query.toLowerCase();
        return name.includes(searchTerm) || description.includes(searchTerm);
      });
      
      setSuggestions(filtered);
    } catch (error) {
      // console.error('Error searching descriptions:', error);
      setSuggestions([]);
    } finally {
      setIsSearching(false);
    }
  }, []);

  // Debounce the search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (formData.description && showSuggestions) {
        searchDescriptions(formData.description);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [formData.description, showSuggestions, searchDescriptions]);

  // Handle selecting a suggestion
  const handleSelectSuggestion = (type) => {
    setFormData(prev => ({
      ...prev,
      description: type.name || type.description || '',
      series: type.series || prev.series,
      material: type.material || prev.material,
      color: type.color || prev.color,
      cost_per_m2: type.average_cost_per_m2 || type.avg_cost_per_m2 || '',
      matched_type_id: type.id
    }));
    setShowSuggestions(false);
    toast.success(`$/m² asignado: ${type.average_cost_per_m2 || type.avg_cost_per_m2 || 0}`);
  };

  // Handle resize of popup
  const handleResizeStart = (e) => {
    e.preventDefault();
    setIsResizing(true);
    
    const startX = e.clientX;
    const startY = e.clientY;
    const startWidth = popupSize.width;
    const startHeight = popupSize.height;

    const handleMouseMove = (moveEvent) => {
      const newWidth = Math.max(300, startWidth + (moveEvent.clientX - startX));
      const newHeight = Math.max(200, startHeight + (moveEvent.clientY - startY));
      setPopupSize({ width: newWidth, height: newHeight });
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error("Solo se permiten archivos de imagen");
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const base64 = event.target.result;
      setImagePreview(base64);
      setFormData(prev => ({ ...prev, image_data: base64 }));
    };
    reader.readAsDataURL(file);
  };

  const handleImagePaste = (e) => {
    const items = e.clipboardData.items;
    for (let item of items) {
      if (item.type.startsWith('image/')) {
        const file = item.getAsFile();
        const reader = new FileReader();
        reader.onload = (event) => {
          const base64 = event.target.result;
          setImagePreview(base64);
          setFormData(prev => ({ ...prev, image_data: base64 }));
        };
        reader.readAsDataURL(file);
        break;
      }
    }
  };

  const handleRemoveImage = () => {
    setImagePreview(null);
    setFormData(prev => ({ ...prev, image_data: null }));
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSubmit = () => {
    if (!formData.item_code && !formData.description) {
      toast.error("Ingresa al menos un código o descripción");
      return;
    }

    const width = parseFloat(formData.width_m) || 0;
    const height = parseFloat(formData.height_m) || 0;
    const quantity = parseInt(formData.quantity) || 1;
    const costPerM2 = parseFloat(formData.cost_per_m2) || 0;
    const area = width * height;
    const calculatedValue = area * costPerM2 * quantity;

    const newItem = {
      id: uuidv4(),
      item_code: formData.item_code,
      description: formData.description,
      series: formData.series,
      material: formData.material,
      color: formData.color,
      width_m: width,
      height_m: height,
      area_m2: area,
      quantity: quantity,
      cost_per_m2: costPerM2,
      calculated_value: calculatedValue,
      observations: formData.observations,
      image_data: formData.image_data,
      matched_type_id: formData.matched_type_id,
      matched_type_name: formData.description
    };

    onAdd(newItem);
    
    // Reset form
    setFormData({
      item_code: "",
      description: "",
      series: "",
      material: "uPVC",
      color: "",
      width_m: "",
      height_m: "",
      quantity: 1,
      cost_per_m2: "",
      observations: "",
      image_data: null,
      matched_type_id: null
    });
    setImagePreview(null);
    setSuggestions([]);
    setShowSuggestions(false);
  };

  const handleClose = () => {
    setFormData({
      item_code: "",
      description: "",
      series: "",
      material: "uPVC",
      color: "",
      width_m: "",
      height_m: "",
      quantity: 1,
      cost_per_m2: "",
      observations: "",
      image_data: null,
      matched_type_id: null
    });
    setImagePreview(null);
    setSuggestions([]);
    setShowSuggestions(false);
    onClose();
  };

  // Calculate preview
  const width = parseFloat(formData.width_m) || 0;
  const height = parseFloat(formData.height_m) || 0;
  const quantity = parseInt(formData.quantity) || 1;
  const costPerM2 = parseFloat(formData.cost_per_m2) || 0;
  const area = width * height;
  const total = area * costPerM2 * quantity;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px]" onPaste={handleImagePaste}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="w-5 h-5 text-blue-600" />
            Agregar Elemento Manual
          </DialogTitle>
          <DialogDescription>
            Ingresa los datos del elemento. Puedes pegar una imagen (Ctrl+V).
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-3 gap-4 py-4">
          {/* Image Section */}
          <div className="col-span-1 space-y-2">
            <Label>Imagen</Label>
            <div 
              className="border-2 border-dashed rounded-lg p-2 aspect-square flex items-center justify-center cursor-pointer hover:bg-slate-50 transition-colors relative"
              onClick={() => fileInputRef.current?.click()}
            >
              {imagePreview ? (
                <>
                  <img 
                    src={imagePreview} 
                    alt="Preview" 
                    className="max-w-full max-h-full object-contain"
                  />
                  <Button
                    variant="destructive"
                    size="icon"
                    className="absolute top-1 right-1 h-6 w-6"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveImage();
                    }}
                  >
                    <X className="w-3 h-3" />
                  </Button>
                </>
              ) : (
                <div className="text-center text-slate-400">
                  <Upload className="w-8 h-8 mx-auto mb-2" />
                  <p className="text-xs">Clic o Ctrl+V</p>
                </div>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="hidden"
            />
          </div>

          {/* Form Fields */}
          <div className="col-span-2 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Código</Label>
                <Input
                  value={formData.item_code}
                  onChange={(e) => handleChange('item_code', e.target.value)}
                  placeholder="V01"
                  className="h-8"
                />
              </div>
              <div>
                <Label className="text-xs">Serie</Label>
                <Input
                  value={formData.series}
                  onChange={(e) => handleChange('series', e.target.value)}
                  placeholder="Serie 25"
                  className="h-8"
                />
              </div>
            </div>

            <div className="relative">
              <Label className="text-xs">Descripción</Label>
              <div className="relative">
                <Input
                  ref={descriptionInputRef}
                  value={formData.description}
                  onChange={(e) => {
                    handleChange('description', e.target.value);
                    setShowSuggestions(true);
                  }}
                  onFocus={() => setShowSuggestions(true)}
                  placeholder="Buscar descripción..."
                  className="h-8 pr-8"
                  data-testid="description-autocomplete"
                />
                {isSearching ? (
                  <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 animate-spin" />
                ) : (
                  <Search className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                )}
              </div>
              
              {/* Resizable Suggestions Popup */}
              {showSuggestions && suggestions.length > 0 && (
                <div
                  ref={popupRef}
                  className="absolute z-50 mt-1 bg-white border border-slate-200 rounded-lg shadow-xl overflow-hidden"
                  style={{
                    width: popupSize.width,
                    height: popupSize.height,
                    left: 0,
                    top: '100%'
                  }}
                  data-testid="description-suggestions-popup"
                >
                  {/* Header */}
                  <div className="bg-slate-100 px-3 py-2 border-b flex justify-between items-center">
                    <span className="text-xs font-medium text-slate-600">
                      {suggestions.length} descripciones encontradas
                    </span>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-5 w-5"
                      onClick={() => setShowSuggestions(false)}
                    >
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                  
                  {/* Suggestions List */}
                  <div 
                    className="overflow-y-auto"
                    style={{ height: popupSize.height - 70 }}
                  >
                    {suggestions.map((type, idx) => (
                      <div
                        key={type.id || idx}
                        className="px-3 py-2 hover:bg-blue-50 cursor-pointer border-b border-slate-100 last:border-0"
                        onClick={() => handleSelectSuggestion(type)}
                      >
                        <div className="font-medium text-sm text-slate-800">
                          {type.name || type.description}
                        </div>
                        <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                          {type.series && <span>Serie: {type.series}</span>}
                          {type.material && <span>Material: {type.material}</span>}
                          {(type.average_cost_per_m2 || type.avg_cost_per_m2) && (
                            <span className="font-bold text-green-600">
                              $/m²: {type.average_cost_per_m2 || type.avg_cost_per_m2}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Resize Handle */}
                  <div
                    className="absolute bottom-0 right-0 w-4 h-4 cursor-se-resize bg-slate-200 hover:bg-slate-300"
                    onMouseDown={handleResizeStart}
                    style={{
                      clipPath: 'polygon(100% 0, 100% 100%, 0 100%)'
                    }}
                  />
                </div>
              )}
              
              {/* Click outside to close */}
              {showSuggestions && (
                <div 
                  className="fixed inset-0 z-40" 
                  onClick={() => setShowSuggestions(false)}
                />
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Material</Label>
                <Select
                  value={formData.material}
                  onValueChange={(value) => handleChange('material', value)}
                >
                  <SelectTrigger className="h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="uPVC">uPVC</SelectItem>
                    <SelectItem value="Aluminio">Aluminio</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Color</Label>
                <Input
                  value={formData.color}
                  onChange={(e) => handleChange('color', e.target.value)}
                  placeholder="Blanco"
                  className="h-8"
                />
              </div>
            </div>

            <div className="grid grid-cols-4 gap-2">
              <div>
                <Label className="text-xs">Ancho (m)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.width_m}
                  onChange={(e) => handleChange('width_m', e.target.value)}
                  placeholder="1.20"
                  className="h-8"
                />
              </div>
              <div>
                <Label className="text-xs">Alto (m)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.height_m}
                  onChange={(e) => handleChange('height_m', e.target.value)}
                  placeholder="1.50"
                  className="h-8"
                />
              </div>
              <div>
                <Label className="text-xs">Cantidad</Label>
                <Input
                  type="number"
                  min="1"
                  value={formData.quantity}
                  onChange={(e) => handleChange('quantity', e.target.value)}
                  className="h-8"
                />
              </div>
              <div>
                <Label className="text-xs">$/m²</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.cost_per_m2}
                  onChange={(e) => handleChange('cost_per_m2', e.target.value)}
                  placeholder="150"
                  className="h-8"
                />
              </div>
            </div>

            <div>
              <Label className="text-xs">Observaciones</Label>
              <Textarea
                value={formData.observations}
                onChange={(e) => handleChange('observations', e.target.value)}
                placeholder="Notas adicionales..."
                className="h-16 resize-none"
              />
            </div>

            {/* Preview */}
            <div className="bg-slate-50 rounded-lg p-3 grid grid-cols-3 gap-2 text-sm">
              <div>
                <span className="text-slate-500">Área:</span>
                <span className="font-mono ml-2">{area.toFixed(4)} m²</span>
              </div>
              <div>
                <span className="text-slate-500">Cant:</span>
                <span className="font-mono ml-2">{quantity}</span>
              </div>
              <div>
                <span className="text-slate-500">Total:</span>
                <span className="font-mono ml-2 font-bold text-blue-600">
                  ${total.toFixed(2)}
                </span>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} className="bg-blue-600 hover:bg-blue-700">
            <Plus className="w-4 h-4 mr-2" />
            Agregar Elemento
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddItemModal;
